import { Box, LinearProgress } from '@mui/material';
import i18n from 'i18n';
import { RQX_HASH_VALUE } from 'lib/config';
import { errorGenerator } from 'lib/errorGenerator';
import { generatoreCode } from 'lib/generateCode';
import { useDump } from 'lib/useDump';
import { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorPage } from 'requiro-ui-theme';
import { IInitContext, IInitProvider, ThemeBoot } from 'types';
import { useAppInitiator } from './AppInitiator';

const Progress = () => (
  <Box sx={{ width: '100%' }}>
    <LinearProgress color="primary" />
  </Box>
);

export let TITLE_BASE = 'Requiro';

/**
 * context
 */
const InitContext = createContext<IInitContext | null>(null);

const InitProvider = ({ children }: IInitProvider) => {
  const [isDefaultApp, setIsDefaultApp] = useState(false);
  const [theme, setTheme] = useState<ThemeBoot>();
  const { boot } = useAppInitiator();
  const { dump, queryInfo } = useDump();
  const { t } = useTranslation();
  const iFrame = document.getElementById('app-iframe') as HTMLIFrameElement;

  useEffect(() => {
    if (dump?.shell?.theme) {
      const themeFound = boot?.theme?.find(
        (themeSingle) => themeSingle.id === dump.shell.theme,
      );
      setTheme(themeFound);
      if (themeFound?.appshell.favicon) {
        changeFavicon(themeFound?.appshell?.favicon);
      }
      if (themeFound?.appshell?.title) {
        TITLE_BASE = themeFound?.appshell?.title;
      }
    }
  }, [dump?.shell?.theme]);

  useEffect(() => {
    if (theme && iFrame) {
      console.log('[InitProvider.useEffect] theme post message: ', theme);
      iFrame.contentWindow.postMessage({ theme }, '*');
    }
    const rqxHashValue = window.localStorage.getItem(RQX_HASH_VALUE);
    const currentHash = window.location.hash;
    console.log(`[InitProvider.useEffect] currentHash: "${currentHash}", rqxHashValue: "${rqxHashValue}"`);
    if (rqxHashValue && iFrame) {
      console.log(`[InitProvider.useEffect] postMessage route: {route:"${currentHash}"}`);
      // OLD CODE
      // iFrame.contentWindow.postMessage({ route: rqxHashValue }, '*');
      // Post message with route currentHash
      iFrame.contentWindow.postMessage({ route: currentHash }, '*');
    } else {
      console.log(`[InitProvider.useEffect] no RQX_HASH_VALUE or iFrame found, skipping postMessage route for "${currentHash}"`);
    }
  }, [iFrame, theme]);

  document.addEventListener('languageChangeEvent', (event) => {
    i18n.changeLanguage(localStorage.getItem('rqxKeycloakLocale'));
  });
  (window as any).getLanguage = i18n.language.substring(0, 2);

  window.onstorage = () => {
    if (localStorage.getItem('i18nextLng') !== i18n.language) {
      i18n.changeLanguage(localStorage.getItem('i18nextLng'));
      if (iFrame) {
        iFrame.contentWindow.postMessage({ locale: i18n.language }, '*');
        // biome-ignore lint/complexity/useArrowFunction: <explanation>
        iFrame.onload = function () {
          iFrame.contentWindow.postMessage({ language: i18n.language }, '*');
          if (theme) {
            console.log(`theme post message: ${theme}`);
            iFrame.contentWindow.postMessage({ theme }, '*');
          }
        };
      }
    }
  };

  useEffect(() => {
    if (dump?.shell) {
      // NOTA: la default app diventa quella dell'hash non quella del dump
      const { defaultApplicationId } = dump.shell;
      const { href, hash } = window.location;

      setIsDefaultApp(
        href.indexOf(`#${defaultApplicationId}`) > -1 ||
        hash.indexOf(`#state`) > -1,
      );
    }
  }, [dump, setIsDefaultApp]);

  const amIUsingTheDefaultApp = () => {
    const { defaultApplicationId } = dump.shell;
    const { hash } = window.location;
    return (
      hash.indexOf(defaultApplicationId) > -1 ||
      hash.indexOf(`#workspace`) === 0
    );
  };

  const contextValue = {
    boot,
    theme,
    dump,
    isDefaultApp,
    setIsDefaultApp,
    amIUsingTheDefaultApp,
  };

  if (queryInfo.isLoading) {
    return <Progress />;
  }

  if (queryInfo.isError) {
    const errorCode = generatoreCode(8).toString();
    errorGenerator(
      errorCode,
      queryInfo.error,
      'apiError dump',
      'Errore chiamata API al backend per recuperare il Dump',
    );
    return (
      <ErrorPage
        code={{ text: errorCode }}
        title={{ text: t('titleErrorPage') }}
        button={{
          text: t('buttons.backToLogin', 'Back to login'),
          blank: false,
          link: '/rqx/session/logout',
        }}
        problem={{
          text: t('textProblemErrorPage'),
          link: t('linkProblemErrorPage'),
        }}
      />
    );
  }

  return dump ? (
    <InitContext.Provider value={contextValue}>{children}</InitContext.Provider>
  ) : (
    <ErrorPage
      code={{ text: '404' }}
      title={{ text: t('titleErrorPage') }}
      button={{ text: t('textButtonErrorPage') }}
      problem={{
        text: t('textProblemErrorPage'),
        link: t('linkProblemErrorPage'),
      }}
    />
  );
};

/**
 * custom hook
 */
export function useInitProvider() {
  const ctx = useContext(InitContext);
  if (!ctx) throw new Error('InitContext cannot be undefined!');
  return ctx;
}

export default InitProvider;

const changeFavicon = (hrefNewFavicon) => {
  let faviconTag: any = document.querySelector("link[rel~='icon']");
  if (!faviconTag) {
    faviconTag = document.createElement('link');
    faviconTag.rel = 'icon';
    document.head.appendChild(faviconTag);
  }
  faviconTag.href = hrefNewFavicon;
};
